import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { inject, isDevMode, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { catchError, from, Observable, of, switchMap, throwError } from 'rxjs';

import { API_URL } from '../tokens/env.token';
import { apiErrorCode } from '../utils/api-error.util';
import { ToastService } from './toast.service';

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const XSRF_COOKIE_NAME = 'XSRF-TOKEN';
const XSRF_HEADER_NAME = 'X-XSRF-TOKEN';
const ANTIFORGERY_ERROR_CODE = 'ANTIFORGERY_TOKEN_EXPIRED';

export function xsrfInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const platformId = inject(PLATFORM_ID);

  if (!isPlatformBrowser(platformId)) {
    return next(req);
  }

  if (!MUTATING_METHODS.has(req.method.toUpperCase())) {
    return next(req);
  }

  const apiUrl = inject(API_URL);
  const toastService = inject(ToastService);

  return next(withXsrfToken(req)).pipe(
    catchError((error: unknown) => {
      if (!isAntiforgeryFailure(error)) {
        return throwError(() => error);
      }

      notifyInDevMode(toastService);

      return from(refreshXsrfToken(apiUrl)).pipe(
        catchError(() => of(false)),
        switchMap(refreshed =>
          refreshed ? next(withXsrfToken(req)) : throwError(() => error)
        )
      );
    })
  );
}

function isAntiforgeryFailure(error: unknown): boolean {
  return (
    error instanceof HttpErrorResponse &&
    error.status === 400 &&
    apiErrorCode(error) === ANTIFORGERY_ERROR_CODE
  );
}

async function refreshXsrfToken(apiUrl: string): Promise<boolean> {
  const response = await fetch(`${apiUrl}/auth/csrf`, {
    credentials: 'include',
  });

  return response.ok;
}

function notifyInDevMode(toastService: ToastService): void {
  if (!isDevMode()) {
    return;
  }

  try {
    toastService.showWarning(
      '[DEV] Antiforgery token expired — refreshing and retrying request.'
    );
  } catch {
    // A developer-only notice must never be able to break the flow it is describing. Thrown from inside
    // a catchError selector it would do exactly that twice over: RxJS would propagate it in place of the
    // HttpErrorResponse — leaving subscribers with an error object that has no response on it — and the
    // recovery below would never run.
  }
}

function withXsrfToken(req: HttpRequest<unknown>): HttpRequest<unknown> {
  const token = readCookie(XSRF_COOKIE_NAME);
  if (!token) {
    return req;
  }
  return req.clone({ setHeaders: { [XSRF_HEADER_NAME]: token } });
}

function readCookie(name: string): string | null {
  const target = `${name}=`;
  const cookies = document.cookie ? document.cookie.split('; ') : [];

  for (const cookie of cookies) {
    if (cookie.startsWith(target)) {
      return decodeURIComponent(cookie.substring(target.length));
    }
  }

  return null;
}
